import { getBuildingDisplayHeight } from '@popety-io/utils'
import React, { useEffect, useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useCustomTheme } from '../../providers/CustomThemeProvider'
import { useResolvedThemeColors } from '../../hooks/useResolvedThemeColors'
import {
  Container,
  Stack,
  Text,
  Loading,
  Icon,
  Button,
  PropertyHeader,
  PageContainer,
  MainContainer,
  ContentWrapper,
  MainContent,
  MapWrapper,
  MapCloseButton,
  MobileMapToggle,
  AnalysisMapSectionDataProvider,
} from '@popety-io/ui'
import { PublicLayout } from '../../components/PublicLayout'
import { publicApiService } from '../../services/publicApiService'
import { PublicEstimateSidebar } from './components/PublicEstimateSidebar'
import { PublicEstimateContent } from './components/PublicEstimateContent'
import { PublicEstimateMap } from './components/PublicEstimateMap'
import { FullHeightContainer } from '../../components/shared'
import {
  MapSectionDataProvider,
  useMapSectionData,
  PublicPricePerSqmProvider,
} from './contexts'
import { PublicAnalysisProvider } from '../../providers/PublicAnalysisProvider'
import { getPublicEstimateEntity } from './utils/publicEstimateEntity'

// Inner component that uses the context
const PublicEstimatePageContent: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { hash } = useParams<{ hash: string }>()

  // Get map section context
  const {
    activeSection: mapActiveSection,
    sectionData,
    hoveredItemId,
    selectedItemId,
    flyToCoordinates,
    onItemClick,
    setSelectedItemId,
  } = useMapSectionData()

  // Theme customization
  const { updateThemeConfig } = useCustomTheme()

  const [estimateData, setEstimateData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeSection, setActiveSection] = useState('summary')
  const [isMobileMapOpen, setIsMobileMapOpen] = useState(false)
  const [mapWidth, setMapWidth] = useState(580)
  const [isResizing, setIsResizing] = useState(false)

  // Resolve theme colors from company/office data
  const resolvedThemeConfig = useResolvedThemeColors(estimateData)

  // Update theme when data is loaded
  useEffect(() => {
    if (estimateData && resolvedThemeConfig) {
      updateThemeConfig(resolvedThemeConfig)
    }
  }, [estimateData, resolvedThemeConfig, updateThemeConfig])

  useEffect(() => {
    const fetchEstimateData = async () => {
      if (!hash) return

      try {
        setLoading(true)
        setError(null)
        const data = await publicApiService.getEstimateByHash(hash)
        if (data === null) {
          setError(t('estimate.failed_to_load'))
          setEstimateData(null)
        } else {
          setEstimateData(data.data)

          // Set language to the share creator's preference
          const userLang = data.data?.user?.language
          if (userLang && ['en', 'fr', 'de', 'it'].includes(userLang)) {
            i18n.changeLanguage(userLang)
          }

          // Set initial active section to first available section
          const availableSections = data.data?.available_sections
          if (availableSections && availableSections.length > 0) {
            setActiveSection(availableSections[0])
          }
        }
      } catch (err) {
        console.error('Failed to fetch property estimate:', err)
        setError(t('estimate.failed_to_load'))
      } finally {
        setLoading(false)
      }
    }

    fetchEstimateData()
  }, [hash])

  const handleSectionChange = (sectionId: string) => {
    setActiveSection(sectionId)
  }

  const toggleMobileMap = () => {
    setIsMobileMapOpen(!isMobileMapOpen)
  }

  // Handle map resize
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return

      const newWidth = window.innerWidth - e.clientX
      // Constrain width between min and max
      const constrainedWidth = Math.max(320, Math.min(800, newWidth))
      setMapWidth(constrainedWidth)
    }

    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing])

  // Extract entity for analysis provider
  const estimateEntity = useMemo(
    () => getPublicEstimateEntity(estimateData),
    [estimateData]
  )

  // Extract property location from estimate data
  const propertyLocation = useMemo(() => {
    const geoCenter =
      estimateData?.value?.estimate?.geo_center ||
      estimateData?.value?.geo_center
    return {
      lat: geoCenter?.lat || 47.3769,
      lon: geoCenter?.lon || 8.5417,
    }
  }, [estimateData])

  // Extract land polygon GeoJSON for map
  const landGeoJson = useMemo(() => {
    const land = estimateData?.value?.lands?.[0]
    if (land?.geo_polygon) {
      return land.geo_polygon
    }
    if (land?.geometry) {
      return {
        type: 'Feature',
        geometry: land.geometry,
        properties: { id: land.id },
      }
    }
    return null
  }, [estimateData])

  // Extract building polygon for 3D visualization
  const buildingGeoJson = useMemo(() => {
    const building = estimateData?.value?.buildings?.[0]
    if (building?.geo_polygon) {
      return building.geo_polygon
    }
    if (building?.geometry) {
      return {
        type: 'Feature',
        geometry: building.geometry,
        properties: { id: building.egid },
      }
    }
    return null
  }, [estimateData])

  // Get building height for 3D extrusion
  const buildingHeight = useMemo(() => {
    const building = estimateData?.value?.buildings?.[0]
    return (
      getBuildingDisplayHeight(building?.height_estimate_to_display) ??
      building?.height_estimate ??
      (building?.height || 10)
    )
  }, [estimateData])

  // Extract area fields for the price-per-m² selector (session-only state)
  const areas = useMemo(
    () => ({
      livingArea: estimateData?.property_attributes?.overview?.livingArea,
      usableArea: estimateData?.property_attributes?.overview?.usableArea,
      weightedArea: estimateData?.property_attributes?.overview?.weightedArea,
    }),
    [estimateData]
  )

  if (!hash) {
    return (
      <PublicLayout>
        <Container>
          <Stack align="center" justify="center" style={{ minHeight: '60vh' }}>
            <Text variant="h3">{t('common.validation.invalid_link')}</Text>
          </Stack>
        </Container>
      </PublicLayout>
    )
  }

  return (
    <PublicLayout
      logo={estimateData?.office_logo}
      officeName={estimateData?.office_name}
      creationDate={estimateData?.create_timestamp}
      title={estimateData?.user_name || ''}
    >
      {loading && (
        <Container>
          <Stack align="center" justify="center" style={{ minHeight: '50vh' }}>
            <Loading size="large" />
            <Text variant="h4">{t('common.loading')}</Text>
          </Stack>
        </Container>
      )}

      {error && (
        <Container>
          <Stack
            align="center"
            justify="center"
            spacing="lg"
            style={{ minHeight: '50vh' }}
          >
            <Icon name="alert-triangle" size="xl" color="error" />
            <Stack align="center" spacing="sm">
              <Text variant="h3" weight="bold">
                {t('estimate.error_loading_data')}
              </Text>
              <Text variant="body1" color="secondary">
                {error}
              </Text>
            </Stack>
            <Button variant="primary" onClick={() => window.location.reload()}>
              {t('common.try_again')}
            </Button>
          </Stack>
        </Container>
      )}

      {estimateData && !loading && !error && (
        <PublicAnalysisProvider
          entity={estimateEntity}
          entityType="estimate"
          userId={hash || ''}
        >
          <AnalysisMapSectionDataProvider>
            <FullHeightContainer>
              <PageContainer
                style={{ top: 0, height: '100%', position: 'relative' }}
              >
                <PropertyHeader
                  title={
                    estimateData?.value_edited?.title ||
                    estimateData?.value?.title ||
                    t('estimate.title')
                  }
                  metadata={{
                    address:
                      estimateData.value?.address ||
                      estimateData.value?.address_full ||
                      estimateData?.value?.address_postal_code_name ||
                      t('common.status.unknown_location'),
                    zone: estimateData.value?.lands?.[0]?.main_lupa_type
                      ? `${estimateData.value?.lands?.[0]?.main_lupa_type || ''} - ${estimateData.value?.lands?.[0]?.main_lupa_name || ''}`
                      : t('common.units.na'),
                    area: estimateData.value?.land_area
                      ? `${estimateData.value.land_area} m²`
                      : t('common.units.na'),
                  }}
                />

                <MainContainer>
                  <PublicEstimateSidebar
                    activeSection={activeSection}
                    onSectionChange={handleSectionChange}
                    availableSections={estimateData?.available_sections}
                  />

                  <ContentWrapper>
                    <MainContent>
                      <PublicPricePerSqmProvider areas={areas}>
                        <PublicEstimateContent
                          activeSection={activeSection}
                          estimateData={estimateData}
                          hash={hash}
                          availableSections={estimateData?.available_sections}
                        />
                      </PublicPricePerSqmProvider>
                    </MainContent>

                    <MapWrapper $isOpen={isMobileMapOpen} $width={mapWidth}>
                      <MapCloseButton onClick={() => setIsMobileMapOpen(false)}>
                        <Icon name="x" size="medium" color="secondary" />
                      </MapCloseButton>
                      <PublicEstimateMap
                        coordinates={[
                          propertyLocation.lon,
                          propertyLocation.lat,
                        ]}
                        onResizeStart={() => setIsResizing(true)}
                        estimateData={estimateData}
                        geoJson={landGeoJson}
                        buildingGeoJson={buildingGeoJson}
                        buildingHeight={buildingHeight}
                        activeSection={mapActiveSection}
                        sectionData={sectionData}
                        hoveredItemId={hoveredItemId}
                        selectedItemId={selectedItemId}
                        flyToCoordinates={flyToCoordinates}
                        onItemClick={onItemClick}
                        setSelectedItemId={setSelectedItemId}
                      />
                    </MapWrapper>
                  </ContentWrapper>
                </MainContainer>

                <MobileMapToggle onClick={toggleMobileMap}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 9m0 8V9m0 0L9 7" />
                  </svg>
                </MobileMapToggle>
              </PageContainer>
            </FullHeightContainer>
          </AnalysisMapSectionDataProvider>
        </PublicAnalysisProvider>
      )}
    </PublicLayout>
  )
}

// Wrapper component with provider
const PublicEstimatePage: React.FC = () => {
  return (
    <MapSectionDataProvider>
      <PublicEstimatePageContent />
    </MapSectionDataProvider>
  )
}

export default PublicEstimatePage
